import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Side-effect: install global axios 401 → refresh-token interceptor (must load early)
import "./utils/axios-interceptor";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import OrgChart from "./pages/OrgChart";
import Departments from "./pages/Departments";
import Payroll from "./pages/Payroll";
import MyPayslips from "./pages/MyPayslips";
import Leaves from "./pages/Leaves";
import Attendance from "./pages/Attendance";
import Loans from "./pages/Loans";
import Advances from "./pages/Advances";
import Analytics from "./pages/Analytics";
import GeoTracking from "./pages/GeoTracking";
import Projects from "./pages/Projects";
import Tasks from "./pages/Tasks";
import Assets from "./pages/Assets";
import Visits from "./pages/Visits";
import Activity from "./pages/Activity";
import CompanySetup from "./pages/CompanySetup";
import Performance from "./pages/Performance";
import Expenses from "./pages/Expenses";
import Onboarding from "./pages/Onboarding";
import Reports from "./pages/Reports";
import SelfOnboarding from "./pages/SelfOnboarding";
import AttendanceAnalytics from "./pages/AttendanceAnalytics";
import Insurance from "./pages/Insurance";
import Profile from "./pages/Profile";
import CRMLeads from "./pages/CRMLeads";
import CRMDashboard from "./pages/CRMDashboard";
import AuditLog from "./pages/AuditLog";
import Goals from "./pages/Goals";
import Engagement from "./pages/Engagement";
import Learning from "./pages/Learning";
import ApplyLoan from "./pages/ApplyLoan";
import NPSRespond from "./pages/NPSRespond";
import ExecutiveView from "./pages/ExecutiveView";
import OnboardingJoin from "./pages/OnboardingJoin";

// Components
import Layout from "./components/Layout";
import InstallPrompt from "./components/InstallPrompt";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], skipOnboardingCheck = false }) => {
    const { user, loading, isAuthenticated } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-slate-500">Loading...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Redirect new hires to self-onboarding
    if (!skipOnboardingCheck && user?.onboarding_completed === false && user?.role !== 'admin') {
        return <Navigate to="/self-onboarding" replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-slate-500">Loading...</div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Self-Onboarding Route (fullscreen, no Layout)
const SelfOnboardingRoute = () => {
    const { user, loading, isAuthenticated } = useAuth();
    if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-slate-500">Loading...</div></div>;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    if (user?.onboarding_completed !== false || user?.role === 'admin') return <Navigate to="/dashboard" replace />;
    return <SelfOnboarding />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            <Route path="/apply" element={<ApplyLoan />} />
            <Route path="/nps-respond/:token" element={<NPSRespond />} />
            <Route path="/join/:token" element={<OnboardingJoin />} />

            {/* Protected Routes - All Users */}
            <Route path="/dashboard" element={
                <ProtectedRoute>
                    <Dashboard />
                </ProtectedRoute>
            } />

            <Route path="/leaves" element={
                <ProtectedRoute>
                    <Leaves />
                </ProtectedRoute>
            } />

            <Route path="/attendance" element={
                <ProtectedRoute>
                    <Attendance />
                </ProtectedRoute>
            } />

            <Route path="/geo-tracking" element={
                <ProtectedRoute>
                    <GeoTracking />
                </ProtectedRoute>
            } />

            <Route path="/my-payslips" element={
                <ProtectedRoute allowedRoles={['employee']}>
                    <MyPayslips />
                </ProtectedRoute>
            } />

            {/* Protected Routes - Admin & HR Manager Only */}
            <Route path="/employees" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Employees />
                </ProtectedRoute>
            } />

            <Route path="/org-chart" element={
                <ProtectedRoute>
                    <OrgChart />
                </ProtectedRoute>
            } />

            <Route path="/departments" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Departments />
                </ProtectedRoute>
            } />

            <Route path="/payroll" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Payroll />
                </ProtectedRoute>
            } />

            <Route path="/loans" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Loans />
                </ProtectedRoute>
            } />

            <Route path="/advances" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Advances />
                </ProtectedRoute>
            } />

            <Route path="/analytics" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Analytics />
                </ProtectedRoute>
            } />

            {/* HRM Mitra Modules - Projects, Tasks, Assets, Visits, Activity */}
            <Route path="/projects" element={
                <ProtectedRoute>
                    <Projects />
                </ProtectedRoute>
            } />

            <Route path="/tasks" element={
                <ProtectedRoute>
                    <Tasks />
                </ProtectedRoute>
            } />

            <Route path="/assets" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Assets />
                </ProtectedRoute>
            } />

            <Route path="/visits" element={
                <ProtectedRoute>
                    <Visits />
                </ProtectedRoute>
            } />

            <Route path="/activity" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Activity />
                </ProtectedRoute>
            } />

            <Route path="/company-setup" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <CompanySetup />
                </ProtectedRoute>
            } />

            <Route path="/performance" element={
                <ProtectedRoute>
                    <Performance />
                </ProtectedRoute>
            } />

            <Route path="/expenses" element={
                <ProtectedRoute>
                    <Expenses />
                </ProtectedRoute>
            } />

            <Route path="/onboarding" element={
                <ProtectedRoute>
                    <Onboarding />
                </ProtectedRoute>
            } />

            <Route path="/reports" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <Reports />
                </ProtectedRoute>
            } />

            {/* Self-Onboarding (fullscreen, no layout) */}
            <Route path="/self-onboarding" element={
                <SelfOnboardingRoute />
            } />

            <Route path="/attendance-analytics" element={
                <ProtectedRoute>
                    <AttendanceAnalytics />
                </ProtectedRoute>
            } />

            <Route path="/insurance" element={
                <ProtectedRoute>
                    <Insurance />
                </ProtectedRoute>
            } />

            <Route path="/profile" element={
                <ProtectedRoute>
                    <Profile />
                </ProtectedRoute>
            } />

            {/* CRM Module */}
            <Route path="/crm-leads" element={
                <ProtectedRoute>
                    <CRMLeads />
                </ProtectedRoute>
            } />

            <Route path="/crm-dashboard" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <CRMDashboard />
                </ProtectedRoute>
            } />

            <Route path="/audit-log" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <AuditLog />
                </ProtectedRoute>
            } />

            <Route path="/goals" element={
                <ProtectedRoute>
                    <Goals />
                </ProtectedRoute>
            } />

            <Route path="/engagement" element={
                <ProtectedRoute>
                    <Engagement />
                </ProtectedRoute>
            } />

            <Route path="/learning" element={
                <ProtectedRoute>
                    <Learning />
                </ProtectedRoute>
            } />

            <Route path="/exec" element={
                <ProtectedRoute allowedRoles={['admin', 'hr_manager']}>
                    <ExecutiveView />
                </ProtectedRoute>
            } />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                </BrowserRouter>
                <Toaster />
                <InstallPrompt />
            </AuthProvider>
        </div>
    );
}

export default App;
